import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  Sport,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerDrawerToken } from 'components/token/PlayerDrawerToken';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';
import { useConversionCreditForPrice } from 'hooks/useConversionCreditForPrice';

import { ManagerSalesHeader } from '../ManagerSalesHeader';
import { PlayerDrawerTokenSkeleton } from '../ui';
import type { PlayerMarketDrawerManagerSales_anyCard } from './__generated__/index.graphql';
import { useManagerSalesTag } from './useManagerSalesTag';

const Root = styled(Vertical).attrs({
  gap: 0,
})`
  flex-grow: 1;
  border-left: 1px solid var(--c-nd-150-opaque);
`;

const Sales = styled(Vertical)`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: 0 var(--double-unit) var(--double-unit);
  @media ${laptopAndAbove} {
    padding: 0 var(--triple-unit) var(--triple-unit);
  }
`;

type Props = {
  anyCards: PlayerMarketDrawerManagerSales_anyCard[];
  slugs: string[];
  count: number;
  primaryOffer?: PlayerMarketDrawerManagerSales_anyCard;
};

export const ManagerSales = ({
  anyCards,
  slugs,
  count,
  primaryOffer,
}: Props) => {
  const { bestPrice: bestManagerSalePrice, highestLevel } = useManagerSalesTag({
    anyCards,
  });
  const primaryOfferLive = useIsPrimaryBuyLive(primaryOffer);
  const { priceDiscountedAmount } = useConversionCreditForPrice({
    price: primaryOfferLive?.price || {
      eur: 0,
      referenceCurrency: SupportedCurrency.EUR,
    },
    sport: primaryOffer?.sport || Sport.FOOTBALL,
  });

  const bestPrimaryPrice = primaryOfferLive?.price && priceDiscountedAmount;

  const bestPrice =
    bestPrimaryPrice &&
    bestManagerSalePrice.price &&
    bestPrimaryPrice.eur < bestManagerSalePrice.price.eur
      ? { slug: null, price: null }
      : bestManagerSalePrice;

  return (
    <Root>
      <ManagerSalesHeader count={count} />
      <Sales>
        {slugs.map(slug => {
          const anyCard = anyCards.find(c => slug === c.slug);
          if (!anyCard) return <PlayerDrawerTokenSkeleton key={slug} />;
          return (
            <PlayerDrawerToken
              key={slug}
              anyCard={anyCard}
              bestPrice={bestPrice.slug === anyCard.slug}
              highestLevel={highestLevel === anyCard.slug}
            />
          );
        })}
      </Sales>
    </Root>
  );
};

ManagerSales.fragments = {
  anyCard: gql`
    fragment PlayerMarketDrawerManagerSales_anyCard on AnyCardInterface {
      slug
      ...useManagerSalesTag_anyCard
      ...PlayerDrawerToken_anyCard
      ...useIsPrimaryBuyLive_anyCard
    }
    ${useManagerSalesTag.fragments.anyCard}
    ${PlayerDrawerToken.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
  ` as TypedDocumentNode<PlayerMarketDrawerManagerSales_anyCard>,
};
